/**
 * The task conversation: how Ask Noticeboard collects the details of a task
 * (a Notice, an Event or a Poll), one question at a time, then shows a
 * summary to confirm. Each task is a schema (its details, in the order they're
 * asked, and how they become a wizard handover); this file is the same for every
 * task. Pure: the record, the message and what it knows about the Organization
 * in, the next record and the reply out. Nothing is created or sent here.
 */
import type { WizardStepName } from "../../notices/wizardSession";
import { cancelKindOf } from "../cancelPhrasing";
import type { HandoffTarget, SlotValues, SummaryLine } from "../catalogue/types";
import { readFollowUp } from "../followUpRules";
import { looksLikeQuestion } from "../sideQuestion";
import { readTaskDetails, resolveDay, type DayKey } from "../taskDetails";
import { isApproval } from "./approval";
import { readTaskEdit } from "./taskEdits";
import { eventTask } from "./eventTask";
import { noticeTask } from "./noticeTask";
import { pollTask } from "./pollTask";
import { YOURS, findRecipients, type Recipient } from "./recipients";
import { nextWeekdayToCheck, recipientInstruction } from "./sharedDetails";

export type TaskName = "notice" | "event" | "poll";

/** What a task needs to know about the Organization: who this Team member may send to, and today. */
export type TaskContext = {
  /** Everyone (if they may send to Everyone) and the Circles they may send to: the choices offered */
  everyoneAndCircles: Recipient[];
  /** Users they may send to by name; never offered as choices */
  users: Recipient[];
  organizationName?: string;
  today: Date;
  /** Asked on the Calendar page, where an Event opens over the Calendar */
  onCalendar?: boolean;
};

/** The running record of a task: the details so far, and the detail just asked for. */
export type TaskRecord = {
  task: TaskName;
  details: Record<string, unknown>;
  /** Previous detail values, retained for explicit undo and restore requests. */
  history?: Record<string, unknown>[];
  asked: string | null;
  /** "done" once it's handed over, kept only so "same for Grade 3" can follow */
  phase: "collecting" | "confirming" | "done";
  /** Answers in a row that couldn't be used, by detail; two opens the wizard there */
  failedTries?: Record<string, number>;
  /** A detail they'll fill in in the wizard ("skip"), which opens there once the rest is known */
  leftForWizard?: string;
  /** Started on the Calendar page, or asked for "on the calendar": an Event opens over the Calendar */
  startedOn?: "calendar";
  /** The request that started it, and the catalogue entry it matched, so "that's not what I meant" can offer what else it might have been */
  startedByText: string;
  startedById: string;
  /** A free-text answer shaped like a question that matched nothing: "Use as title" fills it in with this text instead */
  pendingFreeText?: { fieldLabel: string; text: string };
  /** The two days "next Friday" can mean, while that's being asked: "next Friday" said again is the later one */
  weekdayChoices?: string[];
};

/** What Ask Noticeboard says: a question with choices, or a summary with the wizard it opens. */
export type TaskReply = {
  text: string;
  choices?: string[];
  summary?: SummaryLine[];
  to?: HandoffTarget;
  /** Set on "that's not what I meant": the message to find what else it might have meant, excluding the entry just cancelled */
  offerAlternativesTo?: { text: string; excludingId: string };
  /** A free-text answer shaped like a question, at a step that checks for one: the caller looks it up in the catalogue */
  checkSideQuestion?: { text: string; fieldLabel: string };
};

/** A turn of the conversation; a null record means the task is over. */
export type TaskTurn = { record: TaskRecord | null; reply: TaskReply };

/**
 * `clarifies` when the answer was already dealt with, so it isn't a failed try: it fits
 * more than one ("Which one do you mean?"), or it was a side question answered in the
 * reply itself ("Can parents RSVP?").
 */
export type Question = {
  text: string;
  choices: string[];
  clarifies?: boolean;
  /** "Do you mean Friday 23 October or Friday 30 October?": the two days "next Friday" can mean */
  weekdayChoices?: string[];
};

/** One detail of a task. Required details are asked in schema order; the rest are only taken when said, or asked when said to be wrong. */
export type DetailRule = {
  name: string;
  /**
   * Asked for until it's given, or only while other details need it (a repeat needs
   * the day it stops); the rest are only asked for again when said to be wrong
   */
  required?: boolean | ((details: Record<string, unknown>) => boolean);
  /** What the Team member calls this detail ("the title", "who it goes to"), for "change the title to …" and "the title's wrong" */
  called: RegExp;
  question?: (org: TaskContext) => Question;
  /** The wizard step it's filled in on, when the chat can't get it */
  step?: WizardStepName;
  /** Answers that leave it to be written in the wizard: "skip", "I'll write it there" */
  writtenThere?: RegExp;
  /**
   * Two answers it can't use never hand this detail to the wizard unanswered;
   * only an explicit skip (`writtenThere`) does. Who it's for is never
   * widened by a question or a name it doesn't recognise.
   */
  requiresExplicitSkip?: boolean;
  /** The value in what was said, or the question to ask instead when it can't be used */
  read: (said: string, org: TaskContext) => { value: unknown } | Question;
  /**
   * This detail in an answer to another question, only when it's really there (a Circle
   * this Team member may send to, a day it can read), and the answer without it.
   */
  spot?: (said: string, org: TaskContext) => { value: unknown; rest: string } | undefined;
  /** The part to read when a whole answer is only about this detail ("send it to Everyone"), so it's never kept as text */
  instruction?: (said: string, org: TaskContext) => string | undefined;
  /** The other details picked up from an answer to this one's question, in the order they're looked for */
  alsoReads?: string[];
  /** Only picks those up when this holds: an Event's message gives its day, time and place only when it names a day */
  alsoReadsWhen?: (said: string, org: TaskContext) => boolean;
  /** Kept as written, with the words other details were picked up from: a message, not a title */
  keepsWords?: boolean;
  /** Whether all that was said is a value of this detail ("Grade 3", "urgent", "Friday"), so "not Grade 3" clears it */
  isValue?: (said: string, org: TaskContext) => boolean;
  /** A free-text detail (a title, a message): a question-shaped answer is checked against the catalogue before it's taken as the value */
  checkSideQuestions?: boolean;
};

export type TaskSchema = {
  task: TaskName;
  details: DetailRule[];
  /** Why it can't start for this Team member, if it can't */
  cannotStart: (org: TaskContext) => string | undefined;
  /**
   * The details a request (or a follow-up, with no request) says, by name: as written,
   * to be read and checked, or already read from it.
   */
  saidIn: (said: SlotValues, request: string, org: TaskContext) => Record<string, string | { value: unknown } | undefined>;
  summary: (details: Record<string, unknown>, org: TaskContext) => SummaryLine[];
  /** The wizard, filled in, on Review unless another step is given; `onCalendar` when it was started there */
  handover: (details: Record<string, unknown>, org: TaskContext, opens: { startAt?: WizardStepName; onCalendar: boolean }) => HandoffTarget;
  /** Shown with the summary, and with the handover once confirmed */
  readyText: string;
  handedOverText: string;
  /** Shown when the wizard opens on a detail the chat couldn't get */
  fillInThereText: string;
};

export const CANCELLED_REPLY = "No problem, nothing was created.";
const WHAT_TO_CHANGE = "What would you like to change?";

const TASK_LABELS: Record<TaskName, string> = { notice: "Notice", event: "Event", poll: "Poll" };

/** "Shall we carry on with your Notice?", said after a side question is answered. */
export const carryOnPrompt = (task: TaskName) => `Shall we carry on with your ${TASK_LABELS[task]}?`;

/** "Carry on with your Notice about Sports day?", offered when reopening with a task in progress. */
export function reopenPrompt(record: TaskRecord): string {
  const details = record.details as { title?: string; question?: string };
  const topic = details.title ?? details.question;
  const name = TASK_LABELS[record.task];
  return topic ? `Carry on with your ${name} about ${topic.replace(/[?!]+$/, "")}?` : `Carry on with your ${name}?`;
}

/** The next question or summary for a task in progress, asked again: what "Carry on" resumes with. */
export function resumeTask(record: TaskRecord, org: TaskContext): TaskTurn {
  return nextStep(schemaFor(record.task), record, org);
}

/** "Use as title", "Use as message": takes the question-shaped text as the value after all. */
const USE_AS = /^use (?:it |that )?as\b/i;

/** "no" on the summary, and what follows it: "no, the date's wrong" */
const NO = /^(?:no|nope|not quite|not really)\b[,.!\s]*/i;
/** "change the title to …": the detail and its new value are split on a " to " later on */
const CHANGE =
  /^(?:[^.!?]*[.!?]+\s+)*(?:(?:actually|yes|yeah|yep|no|ok(?:ay)?|please|can you|could you|and|sorry|i told you|that(?:'s| is) (?:wrong|not right))[,.!\s]+)*(?:change|set|update|switch|make|fix)\s+(.+)$/i;
/** "take 'Send to testing.' out of the message", "remove the last line from the title". */
const TAKE_OUT =
  /^(?:(?:please|and|also|can you|could you)[,\s]+)*(?:take|remove|delete|drop|cut)\s+["'“‘]?(.+?)["'”’]?\s+(?:out\s+of|out\s+from|from)\s+(?:the\s+)?(\w+)[\s.!]*$/i;
/** "I want to edit the poll question", "let me fix the title": that detail, asked for again. */
const WANTS_TO_EDIT = /\b(?:edit|change|fix|update|correct|redo)\s+(?:the\s+|my\s+)?(?:poll\s+|notice\s+|event\s+)?(\w+)\b/i;
/** "The title must be PTA Bake Sale, and the place is just Front gate": each detail and what it should be. */
const SHOULD_BE =
  /\b(?:the\s+)?(\w+)\s+(?:must|should|needs\s+to)\s+be\s+(?:just\s+)?(.+?)(?=,?\s+and\s+(?:the\s+)?\w+\s+(?:must|should|needs\s+to|is)\s|[.!]*$)|\band\s+(?:the\s+)?(\w+)\s+is\s+(?:just\s+)?(.+?)[.!]*$/gi;
/** "'Head lice in Grade 3'.": without its quotes, and the stop after them; a stop that's part of it stays. */
const unquoted = (value: string) =>
  value
    .trim()
    .replace(/^["'“‘]+/, "")
    .replace(/["'”’]+[.!]*$/, "")
    .trim();
/** "first option should just be pj day", "change the second choice to Yes": one choice on a Poll's list. */
const ONE_CHOICE =
  /\b(first|second|third|fourth|fifth|sixth|last|1st|2nd|3rd|4th|5th|6th)\s+(?:option|choice)\s+(?:should\s+(?:just\s+)?be|is|to|=)\s+(.+?)[.!]*$/i;
const ORDINALS = ["first", "second", "third", "fourth", "fifth", "sixth"];
/** "the title's wrong", "change the date", or just "the title" */
const WRONG =
  /^(?:(change|fix)\s+)?(.+?)(?:'s|\s+is|\s+are)?(?:\s+(wrong|not right|incorrect))?[\s.!]*$/i;
/** "not Grade 3", "it's not urgent", "it shouldn't go to Grade 3" */
const NOT =
  /^(?:(?:no|nope|actually)[,.!\s]+)*(?:(?:it|that)(?:'s|\s+is|\s+should)?\s+)?(?:not|isn't|shouldn't (?:be|go))\s+(?:(?:to|for|on)\s+)?(.+?)[\s.!]*$/i;
/** An optional detail asked for again can be left off */
const NONE = /^(?:none|no|nope|skip|no (?:date|day)|leave it(?: off| out)?|remove it|there isn't one)[\s.!]*$/i;
/** A request that mentions the calendar: "put sports day on the calendar" */
const MENTIONS_CALENDAR = /\bcalendar\b/i;
/** "ok, looks good", "actually, go ahead": a lead-in word before the confirmation itself */
const CONFIRM =
  /^(?:(?:yes|yep|yeah|yup|sure|ok(?:ay)?|actually)[,\s]+)*(?:yes|yep|yeah|yup|sure|ok(?:ay)?|perfect|great|correct|that's right|looks (?:good|great|right|fine)|go ahead|open it|send it)(?:[,\s]+(?:thanks|thank you|please))?[\s.!]*$/i;

/** "schedule it for tomorrow morning at 7": scheduling isn't something the chat can set yet */
const SCHEDULE_REQUEST = /^(?:(?:actually|please|can you|could you|and)[,\s]+)*schedule\s+(?:it|this|that)?\s*(?:for\s+.+)?[\s.!]*$/i;
const CANT_SCHEDULE_FROM_CHAT =
  "I can't schedule it from here, but on the next screen choose Schedule for later, pick the date and time, and press Schedule.";

/** Every guided task; a new one is a new schema here, not new conversation code. */
const SCHEMAS: Record<TaskName, TaskSchema> = { notice: noticeTask, event: eventTask, poll: pollTask };
const schemaFor = (task: TaskName) => SCHEMAS[task];

const isQuestion = (read: { value: unknown } | Question): read is Question => !("value" in read);

const isRequired = (rule: DetailRule, details: Record<string, unknown>) =>
  typeof rule.required === "function" ? rule.required(details) : Boolean(rule.required);

/** The wizard this record opens, on Review unless another step is given. */
const handoverOf = (schema: TaskSchema, record: TaskRecord, org: TaskContext, startAt?: WizardStepName) =>
  schema.handover(record.details, org, { startAt, onCalendar: record.startedOn === "calendar" });

/**
 * Fills in every detail that was said; the first one that can't be used is asked
 * about again. The details are taken in schema order.
 */
function fillIn(schema: TaskSchema, record: TaskRecord, said: ReturnType<TaskSchema["saidIn"]>, org: TaskContext) {
  const details = { ...record.details };
  let question: (Question & { detail: string }) | undefined;
  for (const rule of schema.details) {
    const value = said[rule.name];
    if (value === undefined) continue;
    const read = typeof value === "string" ? rule.read(value, org) : value;
    if (!isQuestion(read)) details[rule.name] = read.value;
    else if (!question) question = { ...read, detail: rule.name };
  }
  return { record: { ...record, details }, question };
}

/** The summary of what was given, and the wizard it opens. */
const summaryOf = (schema: TaskSchema, record: TaskRecord, org: TaskContext) => ({
  summary: schema.summary(record.details, org),
  to: handoverOf(schema, record, org),
});

/** The wizard opened on this detail's step with everything else filled in: the task is handed over. */
function fillInThere(schema: TaskSchema, record: TaskRecord, detail: string, org: TaskContext): TaskTurn {
  const step = schema.details.find((rule) => rule.name === detail)?.step;
  return {
    record: {
      task: record.task,
      details: record.details,
      asked: null,
      phase: "done",
      startedOn: record.startedOn,
      startedByText: record.startedByText,
      startedById: record.startedById,
      leftForWizard: detail,
    },
    reply: {
      text: schema.fillInThereText,
      summary: schema.summary(record.details, org),
      to: handoverOf(schema, record, org, step),
    },
  };
}

/** The next question, or the summary once nothing required is missing (or the wizard, when a detail is left to it). */
function nextStep(schema: TaskSchema, record: TaskRecord, org: TaskContext): TaskTurn {
  const missing = schema.details.find(
    (rule) => isRequired(rule, record.details) && record.details[rule.name] === undefined && rule.name !== record.leftForWizard
  );
  if (missing?.question) {
    return { record: { ...record, asked: missing.name, phase: "collecting" }, reply: missing.question(org) };
  }
  if (record.leftForWizard) return fillInThere(schema, record, record.leftForWizard, org);
  return {
    record: { ...record, asked: null, phase: "confirming" },
    reply: { text: schema.readyText, ...summaryOf(schema, record, org) },
  };
}

/** The details given, which start their failed tries again, and are no longer left to the wizard. */
function withDetails(record: TaskRecord, details: Record<string, unknown>): TaskRecord {
  const given = (name: string) => name in details;
  const failedTries = Object.fromEntries(Object.entries(record.failedTries ?? {}).filter(([name]) => !given(name)));
  const leftForWizard = record.leftForWizard && !given(record.leftForWizard) ? record.leftForWizard : undefined;
  const changed = Object.entries(details).some(([key, value]) => JSON.stringify(value) !== JSON.stringify(record.details[key]));
  const history = changed ? [...(record.history ?? []), record.details].slice(-20) : record.history;
  return { ...record, details: { ...record.details, ...details }, history, failedTries, leftForWizard };
}

/** The details this rule's answers also say, and the answer without them. */
function spotIn(schema: TaskSchema, rule: DetailRule, said: string, org: TaskContext, known: Record<string, unknown> = {}) {
  const details: Record<string, unknown> = {};
  let rest = said;
  if (rule.alsoReadsWhen && !rule.alsoReadsWhen(said, org)) return { details, rest };
  for (const name of rule.alsoReads ?? []) {
    // A message mentioning a day doesn't change the day already given
    if (rule.keepsWords && known[name] !== undefined) continue;
    const found = schema.details.find((r) => r.name === name)?.spot?.(rest, org);
    if (!found) continue;
    details[name] = found.value;
    rest = found.rest;
  }
  return { details, rest };
}

/**
 * An answer to the question about this detail: anything else it says is picked up,
 * and an answer that's only about another detail ("send it to Everyone") sets that one instead,
 * unless it's written text that the instruction can't be used from, or all of it is a value
 * of the detail asked about.
 */
function answer(schema: TaskSchema, record: TaskRecord, rule: DetailRule, text: string, org: TaskContext): TaskTurn {
  // "12 December" when asked when a repeat stops is that, not a new day
  const others = rule.isValue?.(text, org) ? [] : schema.details;
  for (const other of others) {
    const part = other !== rule ? other.instruction?.(text, org) : undefined;
    if (part === undefined) continue;
    const read = other.read(part, org);
    if (isQuestion(read)) {
      // "Please send it to the office" may be the message itself.
      // "Only Grade 3" is the audience, even while a title is being written.
      if (rule.keepsWords && !other.isValue?.(part, org)) continue;
      return askAgainOrOpenWizard(schema, record, other.name, read, org);
    }
    const changed = withDetails(record, { [other.name]: read.value });
    const turn = nextStep(schema, changed, org);
    if (other.name === "choices" && Array.isArray(read.value)) {
      const listed = read.value.join(" or ");
      return { ...turn, reply: { ...turn.reply, text: `Choices: ${listed}. Nothing is published until you press Publish. ${turn.reply.text}` } };
    }
    return sayingTheNewDay(schema, record, other, changed, turn, org);
  }
  const ending = rule.keepsWords ? endingInstruction(schema, rule, text, org) : undefined;
  const written = ending ? ending.rest : text;
  const said = rule.instruction?.(written, org) ?? written;
  const { details, rest } = spotIn(schema, rule, said, org, record.details);
  const spotted = withDetails(record, { ...details, ...ending?.details });
  const read = rule.read(rule.keepsWords ? said : rest, org);
  // "TEST Grade 4 2027" when asked the time: who it's for, not a time it can't read. That's kept, and the time asked again.
  const isOther = isQuestion(read) && !read.clarifies && !rule.keepsWords
    ? schema.details.find((other) => other !== rule && other.name === "to" && spotted.details[other.name] === undefined && other.isValue?.(text, org))
    : undefined;
  if (isOther && rule.question) {
    const otherRead = isOther.read(text, org);
    if (!isQuestion(otherRead)) {
      const kept = withDetails(spotted, { [isOther.name]: otherRead.value });
      const said = changedLines(schema, spotted, kept, org);
      return asking(kept, rule.name, { ...rule.question(org), text: `${said ? `${said} ` : ""}${rule.question(org).text}` });
    }
  }
  if (isQuestion(read)) return askAgainOrOpenWizard(schema, spotted, rule.name, read, org);
  return withHurtfulWordsNoted(read.value, nextStep(schema, withDetails(spotted, { [rule.name]: read.value }), org));
}

/** Name-calling that would upset whoever reads a Notice. */
const HURTFUL =
  /\b(?:idiots?|morons?|stupid|useless|dumb|losers?|pathetic|incompetent|imbeciles?|lazy\s+(?:idiots?|people|staff)|shut\s+up|hate\s+you|fuck\w*|shit\w*|crap|bastards?|bitch\w*|assholes?)\b/i;

/** "Heads up" before the next step when a title or message calls people names: it can still be changed before Publish. */
function withHurtfulWordsNoted(value: unknown, turn: TaskTurn): TaskTurn {
  const word = typeof value === "string" ? value.match(HURTFUL)?.[0] : undefined;
  if (!word) return turn;
  return {
    ...turn,
    reply: {
      ...turn.reply,
      text: `Heads up: “${word}” could upset the people who read it. You can change it before you press Publish. ${turn.reply.text}`,
    },
  };
}

/**
 * "… in the hall. Send to testing.", "… early collection. Mark it urgent.": a message whose last
 * sentence only says who it goes to or that it's urgent. That detail is set, and the sentence
 * isn't kept in the message.
 */
function endingInstruction(schema: TaskSchema, rule: DetailRule, text: string, org: TaskContext) {
  const last = text.match(/(?:^|[.!?]\s+)([^.!?]+[.!?]*)\s*$/);
  if (!last || last.index === undefined || last.index === 0) return undefined;
  const sentence = last[1].trim();
  for (const other of schema.details) {
    if (other === rule) continue;
    const part = other.instruction?.(sentence, org);
    if (part === undefined) continue;
    const read = other.read(part, org);
    if (isQuestion(read)) return undefined;
    return { details: { [other.name]: read.value }, rest: text.slice(0, last.index + 1).trim() };
  }
  return undefined;
}

/**
 * "actually the 23rd" while asked who it's for: the reply says the new day or time, which
 * nothing else on screen shows until the summary. Other details changed part-way don't need it.
 */
function sayingTheNewDay(
  schema: TaskSchema,
  before: TaskRecord,
  rule: DetailRule,
  after: TaskRecord,
  turn: TaskTurn,
  org: TaskContext
): TaskTurn {
  if ((rule.name !== "day" && rule.name !== "time" && rule.name !== "closes") || turn.record?.phase !== "collecting") return turn;
  const said = changedLines(schema, before, after, org);
  return said ? { ...turn, reply: { ...turn.reply, text: `${said} ${turn.reply.text}` } } : turn;
}

/** "Date: Friday, October 23." for each summary line a change made or changed. */
function changedLines(schema: TaskSchema, before: TaskRecord, after: TaskRecord, org: TaskContext): string {
  const was = new Map(schema.summary(before.details, org).map((line) => [line.label, line.value]));
  return schema
    .summary(after.details, org)
    .filter((line) => was.get(line.label) !== line.value)
    .map((line) => `${line.label}: ${line.value}.`)
    .join(" ");
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a)?.toLowerCase() === JSON.stringify(b)?.toLowerCase();

/** The summary line for a detail already given that this text says again, like "Choices: Pasta, Rice, Sandwiches.", or "". */
function alreadySet(schema: TaskSchema, record: TaskRecord, text: string, org: TaskContext): string {
  const plainOf = (said: string) => said.trim().replace(/[\s.!?]+$/, "").replace(/^(?:the|our)\s+/i, "").toLowerCase();
  const plain = plainOf(text);
  const listed = plain.split(/\s*(?:,|\bor\b|\band\b)\s*/).filter(Boolean);
  for (const other of schema.details) {
    const value = record.details[other.name];
    if (value === undefined || other.name === "to") continue;
    const read = other.isValue?.(text, org) ? other.read(text, org) : undefined;
    const asText = typeof value === "string" ? value : (value as { value?: unknown })?.value;
    const matches =
      (read && !isQuestion(read) && sameValue(read.value, value)) ||
      (typeof asText === "string" && plainOf(asText) === plain) ||
      (Array.isArray(value) && listed.length === value.length && value.every((choice, i) => typeof choice === "string" && plainOf(choice) === listed[i]));
    if (matches) return changedLines(schema, without(record, other.name), record, org).replace(/\s*\n\s*/g, ", ").replace(/([.!?])\.$/, "$1");
  }
  return "";
}

/** Said while there's more to ask before the wizard opens on the detail left for it. */
const LEFT_FOR_THERE = "No problem, you can set that on the next screen.";
function leavingItThere(turn: TaskTurn): TaskTurn {
  if (turn.record?.phase !== "collecting") return turn;
  return { ...turn, reply: { ...turn.reply, text: `${LEFT_FOR_THERE} ${turn.reply.text}` } };
}

const asking = (record: TaskRecord, detail: string, { text, choices, weekdayChoices }: Question): TaskTurn => {
  const next: TaskRecord = { ...record, asked: detail, phase: "collecting", weekdayChoices };
  if (!weekdayChoices) delete next.weekdayChoices;
  return { record: next, reply: { text, choices } };
};

/** Answers in a row it can't use for one detail before the wizard opens there instead. */
const TRIES_BEFORE_WIZARD = 2;

/**
 * An answer that couldn't be used clears that detail, so it's never handed over: it's
 * asked again with the reason or, the second time in a row, the wizard opens on it.
 */
function askAgainOrOpenWizard(
  schema: TaskSchema,
  record: TaskRecord,
  detail: string,
  question: Question,
  org: TaskContext
): TaskTurn {
  if (question.clarifies) return asking(record, detail, question);
  if (record.task === "poll" && detail === "closes" && record.phase === "confirming") {
    return { record, reply: { text: `${question.text} I haven't changed the closing time. ${schema.readyText}`, ...summaryOf(schema, record, org) } };
  }
  const cleared = without(record, detail);
  const rule = schema.details.find((r) => r.name === detail);
  if (rule?.requiresExplicitSkip) return asking(cleared, detail, question);
  const tries = (record.failedTries?.[detail] ?? 0) + 1;
  if (tries >= TRIES_BEFORE_WIZARD) return fillInThere(schema, cleared, detail, org);
  return asking({ ...cleared, failedTries: { ...record.failedTries, [detail]: tries } }, detail, question);
}

const without = (record: TaskRecord, detail: string): TaskRecord => {
  const { [detail]: _cleared, ...details } = record.details;
  return { ...record, details };
};

/** The detail a phrase like "the title" or "who it goes to" names. */
const named = (schema: TaskSchema, phrase: string) =>
  schema.details.find((rule) => rule.called.test(phrase.trim().replace(/^(?:the|its)\s+/i, "")));

/** A detail cleared because it's wrong: asked for again, or left off if it's never asked. */
function askAgain(schema: TaskSchema, record: TaskRecord, rule: DetailRule, org: TaskContext): TaskTurn {
  const cleared = without(record, rule.name);
  return rule.question ? asking(cleared, rule.name, rule.question(org)) : nextStep(schema, cleared, org);
}

/** A detail set to what was said: the next step, or the detail asked about again when it can't be used. */
function setTo(schema: TaskSchema, record: TaskRecord, rule: DetailRule, said: string, org: TaskContext): TaskTurn {
  const read = rule.read(said, org);
  if (isQuestion(read)) return askAgainOrOpenWizard(schema, record, rule.name, read, org);
  if (record.task === "event" && rule.name === "endTime" && typeof record.details.time === "string" && typeof read.value === "string" && read.value <= record.details.time) {
    return { record, reply: { text: "The end time must be after the start time. I've kept the existing times.", ...summaryOf(schema, record, org) } };
  }
  const changed = withDetails(record, { [rule.name]: read.value });
  const turn = sayingTheNewDay(schema, record, rule, changed, nextStep(schema, changed, org), org);
  // "make it 23 october" when it's already the 23rd: say so, rather than showing the same summary as if it changed
  const unchanged = record.phase === "confirming" && record.details[rule.name] !== undefined && !changedLines(schema, record, changed, org);
  // An explicit no-op still has a previous state: "go back" must not undo the original field entry.
  if (unchanged && turn.record) turn.record = { ...turn.record, history: [...(record.history ?? []), record.details].slice(-20) };
  const line = unchanged ? schema.summary(changed.details, org).find((l) => named(schema, l.label)?.name === rule.name) : undefined;
  return line ? { ...turn, reply: { ...turn.reply, text: `That's already set: ${line.label}: ${line.value}. ${turn.reply.text}` } } : turn;
}

const CHANGE_CLAUSE_SEPARATOR = /(?:,\s*|\s+and\s+)(?=(?:(?:change|set|update)\s+)?(?:the\s+)?(?:title|heading|name|message|body|text|place|venue|location|date|day|time|choices|options)\s+(?:to|into)\b)/i;

/**
 * A change of mind, at any step: "change the title to X" sets it, "not Grade 3" clears
 * who it goes to and asks again, and "the title's wrong" asks for the title again (on the
 * summary "the title" alone does, for a detail it can ask about; part-way it needs a
 * "change" or "wrong"). While a message is being written only "change the X to Y" counts,
 * and only when all of Y is the new value: "Change the date to Friday as the hall is
 * booked" is a message. Undefined when it's none of these.
 */
function changeOfMind(schema: TaskSchema, record: TaskRecord, text: string, org: TaskContext, writing: boolean): TaskTurn | undefined {
  const clauses = text.split(CHANGE_CLAUSE_SEPARATOR);
  if (clauses.length > 1 && CHANGE.test(clauses[0])) {
    let current = record;
    for (let i = 0; i < clauses.length; i++) {
      const clause = /^(?:change|set|update)\b/i.test(clauses[i]) ? clauses[i] : `change ${clauses[i]}`;
      const turn = changeOfMind(schema, current, clause, org, writing);
      if (!turn?.record) return undefined;
      current = turn.record;
      if (current.asked) return turn;
    }
    return nextStep(schema, { ...current, history: [...(record.history ?? []), record.details].slice(-20) }, org);
  }
  const change = text.match(CHANGE)?.[1];
  // "change who it goes to to Everyone": the first " to " after a detail's name
  if (change) {
    // "change the title to: X" as well
    for (const split of Array.from(change.matchAll(/\s+(?:to|into)(?=[\s:])/gi))) {
      const changing = named(schema, change.slice(0, split.index));
      const value = unquoted(change.slice((split.index ?? 0) + split[0].length).replace(/^:/, ""));
      if (changing && (!writing || changing.isValue?.(value, org) !== false)) {
        return setTo(schema, record, changing, value, org);
      }
    }
  }
  // "Change the title" while the message is being written: never the message itself
  const shortChange = writing ? text.match(/^(?:change|fix|edit)\s+(?:the\s+)?(\w+)[\s.!]*$/i) : null;
  const shortOne = shortChange ? named(schema, shortChange[1]) : undefined;
  if (shortOne?.question) return askAgain(schema, record, shortOne, org);
  if (writing) return undefined;

  // "first option should just be pj day"
  const oneChoice = text.match(ONE_CHOICE);
  const choicesRule = schema.details.find((r) => r.name === "choices");
  const choices = record.details.choices;
  if (oneChoice && choicesRule && Array.isArray(choices)) {
    const word = oneChoice[1].toLowerCase();
    const at = word === "last" ? choices.length - 1 : ORDINALS.indexOf(word) >= 0 ? ORDINALS.indexOf(word) : Number(word.replace(/\D/g, "")) - 1;
    if (at >= 0 && at < choices.length) {
      const changed = choices.map((choice, i) => (i === at ? unquoted(oneChoice[2]) : choice));
      return setTo(schema, record, choicesRule, changed.join(", "), org);
    }
  }

  const takeOut = text.match(TAKE_OUT);
  const fromRule = takeOut ? named(schema, takeOut[2]) : undefined;
  const current = fromRule ? record.details[fromRule.name] : undefined;
  if (takeOut && fromRule && typeof current === "string") {
    const phrase = unquoted(takeOut[1]);
    const at = current.toLowerCase().indexOf(phrase.toLowerCase());
    if (at >= 0) {
      const shorter = `${current.slice(0, at)}${current.slice(at + phrase.length)}`.replace(/\s{2,}/g, " ").trim();
      return setTo(schema, record, fromRule, shorter, org);
    }
    // Already gone, or never there: said so, with the summary or the step as it stands
    const turn = nextStep(schema, record, org);
    return { ...turn, reply: { ...turn.reply, text: `“${phrase.replace(/[.!]+$/, "")}” isn't in the ${takeOut[2].toLowerCase()}. ${turn.reply.text}` } };
  }

  // "the title must be X, and the place is just Y": each one set
  const shouldBe = Array.from(text.matchAll(SHOULD_BE))
    .map((found) => ({ rule: named(schema, found[1] ?? found[3] ?? ""), value: unquoted(found[2] ?? found[4] ?? "") }))
    .filter((found): found is { rule: DetailRule; value: string } => Boolean(found.rule && found.value));
  if (shouldBe.length > 0) {
    let changed = record;
    for (const { rule, value } of shouldBe) {
      const read = rule.read(value, org);
      if (isQuestion(read)) return askAgainOrOpenWizard(schema, changed, rule.name, read, org);
      changed = withDetails(changed, { [rule.name]: read.value });
    }
    return nextStep(schema, changed, org);
  }

  const not = text.match(NOT)?.[1];
  const notThis = not !== undefined && schema.details.find((rule) => rule.isValue?.(not, org));
  if (notThis) return askAgain(schema, record, notThis, org);

  const afterNo = text.replace(NO, "");
  const wrong = afterNo.match(WRONG);
  const wrongOne = wrong && named(schema, wrong[2]);
  const saysWrong = wrong && (wrong[1] || wrong[3]);
  if (wrongOne && (saysWrong || (record.phase === "confirming" && wrongOne.question))) {
    return askAgain(schema, record, wrongOne, org);
  }
  const editing = record.phase === "confirming" ? text.match(WANTS_TO_EDIT)?.[1] : undefined;
  const editOne = editing ? named(schema, editing) : undefined;
  if (editOne?.question) return askAgain(schema, record, editOne, org);
  return undefined;
}

/**
 * On the summary, "no" alone asks what to change, an answer that's only about one
 * detail ("send it to Everyone", "make it urgent", or "no, Grade 2") changes it, and
 * "schedule it for …" says that can't be done from the chat, keeping the summary.
 */
function onTheSummary(schema: TaskSchema, record: TaskRecord, text: string, org: TaskContext): TaskTurn | null {
  if (SCHEDULE_REQUEST.test(text)) return { record, reply: { text: CANT_SCHEDULE_FROM_CHAT, ...summaryOf(schema, record, org) } };
  // "make it Thursday 29 October instead": the change, without "instead"
  const afterNo = text.replace(NO, "").replace(/[\s,]+instead[\s.!]*$/i, "");
  if (!afterNo) return { record, reply: { text: WHAT_TO_CHANGE, choices: [] } };
  // "can you add "Thank you for your support." at the end": added to the message
  const messageToAddTo = schema.details.find((r) => r.name === "message");
  const added = afterNo.match(ADD_AT_THE_END)?.[1]?.trim();
  if (messageToAddTo && added) {
    const current = typeof record.details.message === "string" ? record.details.message.trim() : "";
    const joined = current ? `${/[.!?]$/.test(current) ? current : `${current}.`} ${added}` : added;
    const turn = setTo(schema, record, messageToAddTo, joined, org);
    return { ...turn, reply: { ...turn.reply, text: `I've added that to the end of the message. ${turn.reply.text}` } };
  }
  // "Pride and Prejudice, Great Expectations, Jane Eyre" typed at a Poll's summary: its choices, said again
  const choicesRule = schema.details.find((r) => r.name === "choices");
  if (choicesRule && /,/.test(afterNo) && !looksLikeQuestion(afterNo)) {
    const read = choicesRule.read(afterNo, org);
    if (!isQuestion(read)) return setTo(schema, record, choicesRule, afterNo, org);
  }
  // "wait thats today?? i said NEXT friday. make it 23 october": the last sentence says what it should be;
  // "sorry, the date is wrong, make it Thursday 29 October": or the last part after a comma
  const lastSentence = afterNo.match(/[.!?]\s+([^.!?]+[.!?]*)$/)?.[1];
  const lastClause = afterNo.match(/,\s+([^,]+)$/)?.[1];
  for (const said of [afterNo, lastSentence, lastClause].filter((part): part is string => Boolean(part))) {
    const changed = said !== afterNo ? changeOfMind(schema, record, said, org, false) : undefined;
    if (changed) return changed;
    for (const rule of schema.details) {
      const part = rule.instruction?.(said, org);
      if (part !== undefined) return setTo(schema, record, rule, part, org);
    }
  }
  const value = afterNo !== text && schema.details.find((rule) => rule.isValue?.(afterNo, org));
  if (value) return setTo(schema, record, value, afterNo, org);
  // "Room 12" when the place is already Room 12: said so, with the summary as it stands
  const same = schema.summary(record.details, org).find((line) => line.value.toLowerCase() === withoutEnd(afterNo).toLowerCase());
  if (same) return { record, reply: { text: `That's already set: ${same.label}: ${same.value}. ${schema.readyText}`, ...summaryOf(schema, record, org) } };
  // "There is a maths test on Thursday on fractions. Please help…": a fuller message, typed at the summary
  const messageRule = schema.details.find((r) => r.name === "message");
  if (messageRule && soundsLikeAMessage(text)) {
    const turn = setTo(schema, record, messageRule, text, org);
    return { ...turn, reply: { ...turn.reply, text: `I've changed the message. ${turn.reply.text}` } };
  }
  return null;
}

/** "add "Thank you for your support." at the end", "put "See you there!" at the bottom of the message". */
const ADD_AT_THE_END =
  /^(?:(?:can|could|would)\s+you\s+(?:please\s+)?|please\s+)?(?:add|put|append|include)\s+["“'‘]?(.+?)["”'’]?\s+(?:at|to)\s+the\s+(?:end|bottom)(?:\s+of\s+(?:the\s+|it|my\s+)?(?:message|notice|text)?)?[\s.!?]*$/i;

const withoutEnd = (text: string) => text.trim().replace(/[\s.!?]+$/, "");

/** Written to parents, not to Ask: a sentence of six words or more, ending in a full stop or "!", that isn't a question. */
export function soundsLikeAMessage(text: string): boolean {
  const said = text.trim();
  return said.split(/\s+/).length >= 6 && /[.!]$/.test(said) && !looksLikeQuestion(said) && !/^(?:i|we|you|can|could|keep|not|let|actually|undo|move|make|change|set|add|remove|replace|start|forget|do not|please\s+(?:can|could|keep|change|move|make))\b/i.test(said);
}

/** Words that say who something is for, so a name with them that isn't found is worth saying so: "Grade 3 parents". */
const SOUNDS_LIKE_A_CIRCLE = /\b(?:grade|class|parents|families|staff|team|group|circle|learners|students|teachers)\b/i;

/** "my class", "our kids", "my learners": the person's own group, not a Circle's name. */

/**
 * Who a request says it's for, unless that's really part of a sentence ("a poll for the Book Club's
 * next read. The title must be"): a name it can't find is only asked about when it sounds like a Circle.
 */
function audienceWorthReading(schema: TaskSchema, said: ReturnType<TaskSchema["saidIn"]>, org: TaskContext) {
  const to = said.to;
  const rule = schema.details.find((r) => r.name === "to");
  if (typeof to !== "string" || !rule) return said;
  // "my class", "our learners": no Circle is named, so who it goes to is asked once the rest is written
  if (YOURS.test(to.trim())) return { ...said, to: undefined };
  // "a poll for parents": which parents isn't said, so it's asked rather than taken as Everyone
  if (/^(?:the\s+)?(?:parents|families)$/i.test(to.trim())) return { ...said, to: undefined };
  const read = rule.read(to, org);
  if (!isQuestion(read) || read.clarifies) return said;
  const plain = to.split(/\s+/).length <= 4 && !/[.!?:;]/.test(to) && SOUNDS_LIKE_A_CIRCLE.test(to);
  return plain ? said : { ...said, to: undefined };
}

/**
 * Starts a task from the request that asked for it, using every detail it already says. `also` is
 * what was said with the request (a message after it), or details carried over from a task of another kind.
 */
export function startTask(
  task: TaskName,
  request: string,
  startedById: string,
  org: TaskContext,
  also: { said?: Record<string, string>; details?: Record<string, unknown> } = {}
): TaskTurn {
  const schema = schemaFor(task);
  if(task==="event" && !org.everyoneAndCircles.length && !/\bevent\b/i.test(request) && !readTaskDetails(request).said.staffOnly) return {record:null,reply:{text:"You don't have any Circles to send this to. You can explicitly ask for an Internal team only Event."}};
  const cannot = schema.cannotStart(org);
  if (cannot) {
    return { record: null, reply: { text: cannot } };
  }
  const carried = Object.entries(also.details ?? {}).filter(
    ([name, value]) => value !== undefined && schema.details.some((rule) => rule.name === name)
  );
  const blank: TaskRecord = {
    task,
    details: Object.fromEntries(carried),
    asked: null,
    phase: "collecting",
    startedByText: request,
    startedById,
    ...(org.onCalendar || MENTIONS_CALENDAR.test(request) ? { startedOn: "calendar" as const } : {}),
  };
  const fromRequest = audienceWorthReading(schema, schema.saidIn(readTaskDetails(request).said, request, org), org);
  const saidWith = Object.fromEntries(Object.entries(also.said ?? {}).filter(([name]) => name === "title" || fromRequest[name] === undefined));
  const filled = fillIn(schema, blank, { ...fromRequest, ...saidWith }, org);
  // "…in the school hall. 5 November, 9am to 1pm": what a message said with the request gives, as an answer would
  const messageRule = schema.details.find((r) => r.name === "message");
  const message = typeof saidWith.message === "string" ? saidWith.message : undefined;
  const spotted = message && messageRule ? spotIn(schema, messageRule, message, org, filled.record.details).details : {};
  const { record, question } = { ...filled, record: withDetails(filled.record, spotted) };
  if (question) return askAgainOrOpenWizard(schema, record, question.detail, question, org);
  // "an event next friday…" said on a Tuesday: which Friday is asked before anything else
  const dayToCheck = task === "event" && record.details.day !== undefined ? nextWeekdayToCheck(request, org.today) : undefined;
  if (dayToCheck) return asking(without(record, "day"), "day", { ...dayToCheck, text: `Sure. ${dayToCheck.text}` });
  const turn = nextStep(schema, record, org);
  const sure = MENTIONS_CALENDAR.test(request) ? "Sure. I'll add it to the Calendar as an Event. " : "Sure. ";
  return turn.record?.phase === "collecting" ? { ...turn, reply: { ...turn.reply, text: `${sure}${turn.reply.text}` } } : turn;
}

/**
 * The next turn of a task already under way. Null when the message isn't part of
 * it, for the rest of Ask Noticeboard to answer: anything but Looks good or
 * Cancel on the summary, or anything but a follow-up once it's handed over.
 */
/** Only context-dependent commands need task data before ordinary question matching. */
export function isContextualTaskRequest(record: TaskRecord, text: string): boolean {
  return readTaskEdit(record, text) !== null;
}

/** Follow-ups whose meaning comes from the open task, before unrelated catalogue actions. */
export function contextualTaskTurn(record: TaskRecord, message: string, org: TaskContext): TaskTurn | null {
  const edit = readTaskEdit(record, message);
  if (!edit) return null;
  const schema = schemaFor(record.task);
  const text = message.trim().replace(/[.!?]+$/, "");
  if (edit.kind === "appendMessage") {
    const rule = schema.details.find((detail) => detail.name === "message");
    if (!rule) return null;
    const turn = setTo(schema, record, rule, `${record.details.message} ${edit.value}`, org);
    return { ...turn, reply: { ...turn.reply, text: `I've added that to the end of the message. ${turn.reply.text}` } };
  }
  if (edit.kind === "field") {
    if (message.split(CHANGE_CLAUSE_SEPARATOR).length > 1) {
      return changeOfMind(schema, record, message, org, record.phase === "collecting" && record.asked === "message") ?? null;
    }
    const rule = schema.details.find((detail) => detail.name === edit.field);
    if (!rule || (record.asked === "message" && rule.name !== "message" && rule.isValue?.(edit.value, org) === false)) return null;
    return setTo(schema, record, rule, unquoted(edit.value), org);
  }
  if (edit.kind === "pollTitle") {
    const question = typeof record.details.question === "string" ? record.details.question : undefined;
    const kept = question ? ` I've kept the question as “${question}”${/[?!]$/.test(question) ? "" : "."}` : "";
    const instead = edit.value ? `“Change the question to ${edit.value}”` : "“Change the question to …”";
    const said = `A Poll doesn't have a separate title: parents see its question at the top.${kept} To change it, say ${instead}.`;
    // Half-way through, the step it was on is asked again after it
    const step = nextStep(schema, record, org);
    return { ...step, reply: { ...step.reply, text: `${said} ${record.phase === "confirming" ? "" : step.reply.text}`.trim() } };
  }
  if (edit.kind === "undo" && edit.field) {
    const field = edit.field;
    const label = field === "to" ? "audience" : field === "day" ? "date" : field;
    const current = JSON.stringify(record.details[field]);
    const history = record.history ?? [];
    // The last value that field had before it became what it is now
    const at = history.map((d) => d[field]).map((v, i) => [v, i] as const).reverse().find(([v]) => v !== undefined && JSON.stringify(v) !== current)?.[1];
    if (at === undefined) return { record, reply: { text: `There isn't an earlier ${label} to go back to.`, ...summaryOf(schema, record, org) } };
    const turn = nextStep(schema, { ...record, details: { ...record.details, [field]: history[at][field] }, history: [...history, record.details].slice(-20), asked: null }, org);
    return { ...turn, reply: { ...turn.reply, text: `I've put the ${label} back. ${turn.reply.text}` } };
  }
  if (edit.kind === "undo") {
    const history = record.history ?? [];
    if (!history.length) return { record, reply: { text: "There isn't an earlier change to undo.", ...summaryOf(schema, record, org) } };
    const same = JSON.stringify(history[history.length - 1]) === JSON.stringify(record.details);
    const turn = nextStep(schema, { ...record, details: history[history.length - 1], history: history.slice(0, -1), asked: null }, org);
    return { ...turn, reply: { ...turn.reply, text: `${same ? "That request hadn't changed the draft; I've kept the previous values." : "I've undone the last change."} ${turn.reply.text}` } };
  }
  if (edit.kind === "recurrence") {
    const until = edit.value.match(/\b(?:until|till|til)\s+(.+)$/i)?.[1];
    const untilRule = schema.details.find((rule) => rule.name === "repeatUntil");
    if (until && untilRule) {
      const checked = untilRule.read(until, org);
      if (isQuestion(checked)) return { record, reply: { text: checked.text, ...summaryOf(schema, record, org) } };
      if (typeof checked.value === "string" && typeof record.details.day === "string" && resolveDay(checked.value as DayKey, org.today) < resolveDay(record.details.day as DayKey, org.today)) {
        return { record, reply: { text: "The repeat end date must not be before the first Event date. I've kept the existing dates.", ...summaryOf(schema, record, org) } };
      }
    }
    const read = schema.saidIn({}, `create an event repeating ${edit.value}`, org);
    const filled = fillIn(schema, record, { repeat: read.repeat, repeatUntil: read.repeatUntil }, org);
    if (filled.question) return { record, reply: { text: filled.question.text, ...summaryOf(schema, record, org) } };
    return nextStep(schema, withDetails(record, filled.record.details), org);
  }
  if (edit.kind === "restoreMessage") {
    const original = record.history?.find((details) => typeof details.message === "string")?.message;
    const restored = original ? withDetails(record, { message: original }) : record;
    const remaining = text.replace(/^.*?original message[.!]\s*/i, "").replace(/^i meant\s+/i, "");
    const corrected = remaining !== text ? changeOfMind(schema, restored, remaining, org, false) : undefined;
    return corrected ?? nextStep(schema, restored, org);
  }
  if (edit.kind === "restart") return nextStep(schema, withDetails({ ...record, details: {} }, { title: record.details.title }), org);
  if (edit.kind === "clearDate") {
    const day = schema.details.find((r) => r.name === "day");
    if (day) return askAgain(schema, record, day, org);
  }
  if (edit.kind === "eventSettings") {
    const instruction = edit.reminder
      ? "Set automatic Event reminders under Remind members on Settings in the form. This is separate from sending a reminder for an existing Notice."
      : "Set the start and end date and time on Details in the form. I haven't changed those settings here.";
    return { record, reply: { text: `${instruction} ${schema.readyText}`, ...summaryOf(schema, record, org) } };
  }
  if (edit.kind === "offset") {
    const unit = edit.unit;
    const delta = edit.amount;
    if (/^(?:hour|minute)/.test(unit)) {
      const current = record.details.time;
      if (typeof current !== "string") return { record, reply: { text: "What start time should I change it from?" } };
      const [h, m] = current.split(":").map(Number);
      const minutes = h * 60 + m + delta * (unit.startsWith("hour") ? 60 : 1);
      if (minutes < 0 || minutes >= 1440) return { record, reply: { text: "That crosses midnight. Please give the new date and start time explicitly.", ...summaryOf(schema, record, org) } };
      const value = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
      return setTo(schema, record, schema.details.find((r) => r.name === "time")!, value, org);
    }
    if (!record.details.day) return { record, reply: { text: "What date should I change it from?" } };
    const date = resolveDay(record.details.day as DayKey, org.today);
    let days = delta * (unit.startsWith("week") ? 7 : 1);
    if (edit.nextMonday) days = (1 - date.getDay() + 7) % 7 || 7;
    date.setDate(date.getDate() + days);
    return setTo(schema, record, schema.details.find((r) => r.name === "day")!, `${date.getDate()} ${date.toLocaleString("en-US", { month: "long" })} ${date.getFullYear()}`, org);
  }
  if (edit.kind === "choices") {
    const turn=setTo(schema, record, schema.details.find((r) => r.name === "choices")!, edit.values.join(", "), org);
    return /\badd\b/i.test(text) && JSON.stringify(edit.values)===JSON.stringify(record.details.choices) ? {...turn,reply:{...turn.reply,text:`That choice is already there; I won't add it twice. ${turn.reply.text}`}} : turn;
  }
  if (edit.kind === "summary") {
    const day = record.details.day;
    const year = /^what year/i.test(text) && day ? `The year is ${resolveDay(day as DayKey, org.today).getFullYear()}. ` : "I’ve kept your existing details. ";
    const turn = nextStep(schema, record, org);
    return { ...turn, reply: { ...turn.reply, text: year + turn.reply.text } };
  }
  return null;
}

export function continueTask(record: TaskRecord, message: string, org: TaskContext): TaskTurn | null {
  const schema = schemaFor(record.task);
  const text = message.trim();

  if (record.phase === "done") {
    if (cancelKindOf(text) === "cancel") return { record: null, reply: { text: CANCELLED_REPLY } };
    // "only Fugazi General" after the form opened: that Circle, then the next missing detail.
    const audience = recipientInstruction(text, org.everyoneAndCircles, org);
    const found = audience ? findRecipients(org.everyoneAndCircles, audience, org.organizationName) : [];
    if (found.length === 1) {
      const resumed: TaskRecord = { ...record, phase: "collecting", asked: null };
      return nextStep(schema, withDetails(resumed, { to: found[0] }), org);
    }
    const leftThere = schema.details.find((rule) => rule.name === record.leftForWizard);
    // "skip, I'll fill it in there" again: the same handover, not a new question
    if (leftThere?.writtenThere?.test(text)) return fillInThere(schema, record, leftThere.name, org);
    // "everyone" just after skipping who it goes to: the answer to that skipped question after all
    if (leftThere?.name === "to" && text.split(/\s+/).length <= 4) {
      const named = findRecipients(org.everyoneAndCircles, text.replace(/[.!?]+$/, ""), org.organizationName);
      if (named.length === 1) {
        const resumed: TaskRecord = { ...record, phase: "collecting", asked: null };
        return nextStep(schema, withDetails(resumed, { to: named[0] }), org);
      }
    }
    const change = readFollowUp(text);
    if (!change) return null;
    const { record: changed, question } = fillIn(schema, record, schema.saidIn(change, "", org), org);
    // A follow-up naming nothing that can be found may not be a follow-up at all
    return question ? null : nextStep(schema, changed, org);
  }

  const cancel = cancelKindOf(text);
  if (cancel === "cancel") return { record: null, reply: { text: CANCELLED_REPLY } };
  if (cancel === "notWhatIMeant") {
    return {
      record: null,
      reply: {
        text: CANCELLED_REPLY,
        offerAlternativesTo: { text: record.startedByText, excludingId: record.startedById },
      },
    };
  }

  if (record.pendingFreeText) {
    const { fieldLabel, text: original } = record.pendingFreeText;
    const pendingRule = schema.details.find((r) => r.name === fieldLabel);
    const cleared: TaskRecord = { ...record, pendingFreeText: undefined };
    if (pendingRule && USE_AS.test(text)) return answer(schema, cleared, pendingRule, original, org);
    // "Friday" after "Is that the day, or a question for me?": the day, given now
    if (pendingRule?.isValue?.(text, org)) return answer(schema, cleared, pendingRule, text, org);
    if (pendingRule?.question) return asking(cleared, pendingRule.name, pendingRule.question(org));
    return nextStep(schema, cleared, org);
  }

  const contextual = contextualTaskTurn(record, message, org);
  if (contextual) return contextual;

  const rule = schema.details.find((r) => r.name === record.asked);
  // "next Friday" again, after "Do you mean Friday 23 October or Friday 30 October?": the one after this week's
  if (rule && record.weekdayChoices?.length === 2 && /^(?:(?:no|i said|i mean|i meant)[,\s]+)*next\s+\w+day[\s.!]*$/i.test(text)) {
    const { weekdayChoices, ...asked } = record;
    return answer(schema, asked, rule, weekdayChoices[1], org);
  }
  // "skip" when asked for the message: the wizard opens on its step once the rest is known
  if (rule?.writtenThere?.test(text.replace(/^not everyone[.!]\s*/i, ""))) {
    const left = nextStep(schema, { ...record, leftForWizard: rule.name }, org);
    // "it's all day": the times are set there, and what's left is asked here first
    return rule.name === "time" ? leavingItThere(left) : left;
  }
  // "no date" when asked for a date again: it's left off
  if (rule && !isRequired(rule, record.details) && NONE.test(text)) return nextStep(schema, without(record, rule.name), org);

  // "add Saturday as another choice": one more choice on the Poll's list, checked like the rest
  const adding = addedChoice(text);
  const choicesRule = schema.details.find((r) => r.name === "choices");
  const choicesSoFar = record.details.choices;
  if (adding && choicesRule && Array.isArray(choicesSoFar)) {
    if(choicesSoFar.some((choice)=>String(choice).toLowerCase()===adding.toLowerCase())) {
      const kept=nextStep(schema,record,org);
      return {...kept,reply:{...kept.reply,text:`That choice is already there; I won't add it twice. ${kept.reply.text}`}};
    }
    return setTo(schema, record, choicesRule, [...choicesSoFar, adding].join(", "), org);
  }

  // "perfect", "lgtm", "ya looks gd" on the summary: a yes, before anything reads it as a change
  if (record.phase === "confirming" && isApproval(text)) {
    return { record: { ...record, phase: "done" }, reply: { text: schema.handedOverText, ...summaryOf(schema, record, org) } };
  }

  const changed = changeOfMind(schema, record, text, org, Boolean(rule?.keepsWords));
  if (changed) return changed;

  if (record.phase === "confirming") {
    // "skip, I'll fill it in there" on the summary: open the form, as Looks good does
    if (CONFIRM.test(text) || schema.details.some((r) => r.writtenThere?.test(text))) {
      return {
        record: { ...record, phase: "done" },
        reply: { text: schema.handedOverText, ...summaryOf(schema, record, org) },
      };
    }
    // "same for Blackbrick Umhlanga" before this one is open: one audience each, so a copy comes after
    const sameFor = text.match(SAME_FOR)?.[1];
    const other = sameFor ? findRecipients(org.everyoneAndCircles, sameFor, org.organizationName) : [];
    const current = (record.details.to as Recipient | undefined)?.name;
    if (other.length === 1 && current && other[0].name !== current) {
      const kind = record.task === "poll" ? "Poll" : record.task === "event" ? "Event" : "Notice";
      return {
        record,
        reply: {
          text: `A ${kind} goes to one audience, so this one stays with ${current}. Press Looks good to open it, then say “same for ${other[0].name}” and I'll start a copy for ${other[0].name}.`,
          ...summaryOf(schema, record, org),
        },
      };
    }
    // "Blackbrick Umhlanga" on its own at the summary: who it goes to, changed (or said again)
    const toRule = schema.details.find((r) => r.name === "to");
    if (toRule && text.split(/\s+/).length <= 4) {
      // "testing. obviously." as well as "testing"
      const read = toRule.read(text, org);
      if (!isQuestion(read)) return nextStep(schema, withDetails(record, { to: read.value }), org);
    }
    return onTheSummary(schema, record, text, org);
  }

  if (!rule) return nextStep(schema, record, org);
  // "pasta, rice, sandwiches" when asked who it's for, with those choices already taken from the request: said so, and asked again
  if (rule.name === "to" && !rule.isValue?.(text, org) && !rule.instruction?.(text, org)) {
    const restated = alreadySet(schema, record, text, org);
    if (restated) {
      const turn = nextStep(schema, record, org);
      return { ...turn, reply: { ...turn.reply, text: `That's already set: ${restated} ${turn.reply.text}` } };
    }
  }
  // "There is no school on Monday because of the public holiday." when asked who it's for: the message,
  // written out in full, then who it's for asked again
  const messageRule = schema.details.find((r) => r.name === "message");
  if (rule.name === "to" && messageRule && soundsLikeAMessage(text) && !rule.isValue?.(text, org) && !rule.instruction?.(text, org)) {
    const turn = nextStep(schema, withDetails(record, { message: text }), org);
    return { ...turn, reply: { ...turn.reply, text: `I've used that as the message. ${turn.reply.text}` } };
  }
  if (rule.name === "to" && looksLikeQuestion(text)) {
    const read = rule.read(text, org);
    if (isQuestion(read) && !read.text.includes("That sounds like a question for me")) return asking(record, rule.name, read);
    return { record, reply: { text: "", checkSideQuestion: { text, fieldLabel: rule.name } } };
  }
  // A question-shaped answer that's neither a correction nor a skip may be a side question, not the value.
  // A Poll's question is a question, so only one about Noticeboard itself is a side question there.
  const aboutNoticeboard = rule.name === "question" && ASKS_ABOUT_NOTICEBOARD.test(text) && NOTICEBOARD_WORDS.test(text);
  const questionAtChoices = rule.name === "choices" && !/[,;\n]|\sor\s/i.test(text) && (looksLikeQuestion(text) || /^(?:when|which)\b/i.test(text));
  if ((rule.checkSideQuestions && looksLikeQuestion(text)) || aboutNoticeboard || questionAtChoices) {
    return { record, reply: { text: "", checkSideQuestion: { text, fieldLabel: rule.name } } };
  }
  // At a step that wants a day, a time, a place or choices, a question that isn't one of those is a side
  // question too: "will this go to every parent??" while asked the day. Who it's for answers its own questions.
  if (!rule.keepsWords && rule.name !== "to" && looksLikeQuestion(text) && !rule.isValue?.(text, org)) {
    const read = rule.read(text, org);
    if (isQuestion(read) && !read.clarifies) {
      return { record, reply: { text: "", checkSideQuestion: { text, fieldLabel: rule.name } } };
    }
  }
  return answer(schema, record, rule, text, org);
}

/** "what's the difference between…", "how do I…", "can parents see…": asking Ask, not parents. */
const ASKS_ABOUT_NOTICEBOARD =
  /\b(?:difference\s+between|how\s+many\s+(?:events|notices|drafts)\s+(?:do\s+we\s+have|have\s+we\s+sent)|which\s+notices\s+are\s+scheduled|where(?:'s|\s+is)\s+(?:the\s+)?calendar|how\s+(?:do|does|can)\s+(?:i|we|it|parents)|can\s+(?:i|parents|staff)\s+(?:see|make|add|change|edit|close)|where\s+(?:do|can)\s+i|what(?:'s|\s+is)\s+(?:a|an)\s+(?:poll|claim))\b/i;
const NOTICEBOARD_WORDS = /\b(?:polls?|claims?(?:\s+lists?)?|notices?|circles?|events?|publish|noticeboard|anonymous|votes?|voted)\b/i;

/** "same for Grade 3", "and the same to Blackbrick Umhlanga": a copy for someone else. */
const SAME_FOR = /^(?:and\s+)?(?:the\s+)?same\s+(?:for|to)\s+(.+?)[\s.!?]*$/i;

/** "add Saturday as another choice", "add one more option called Saturday": the choice added. */
const ADD_AS_CHOICE = /^(?:(?:please|and|also|oh|ok(?:ay)?|can you|could you)[\s,]+)*add\s+(.+?)\s+as\s+(?:an?|another|one more|a new)\s+(?:choice|option)[\s.!]*$/i;
const ADD_A_CHOICE =
  /^(?:(?:please|and|also|oh|ok(?:ay)?|can you|could you)[\s,]+)*add\s+(?:an?|another|one more|a new)\s+(?:choice|option)[\s:,]+(?:called\s+|named\s+)?(.+?)[\s.!]*$/i;

function addedChoice(text: string): string | undefined {
  const found = text.match(ADD_AS_CHOICE)?.[1] ?? text.match(ADD_A_CHOICE)?.[1];
  const choice = found?.replace(/^["“'‘]|["”'’]$/g, "").trim();
  return choice || undefined;
}

/** A task that's been handed over is done; Looks good marks it so. */
export const handedOver = (record: TaskRecord): TaskRecord => ({ ...record, phase: "done" });

/**
 * Whether this message is one of the open task's details, said on its own: an answer to the
 * question asked, or only about another detail ("staff only", "make it urgent"). Such a message
 * belongs to the task even when it's also, word for word, a catalogue question.
 */
export function takesAsADetail(record: TaskRecord, message: string, org: TaskContext): boolean {
  if (record.phase === "done") return false;
  const schema = schemaFor(record.task);
  const text = message.trim();
  const asked = record.asked ? schema.details.find((rule) => rule.name === record.asked) : undefined;
  if (asked?.isValue?.(text, org)) return true;
  return schema.details.some((rule) => rule.instruction?.(text, org) !== undefined);
}
